import { useState } from "react";
import ModalOverlay from "../../components/UI/ModalOverlay/ModalOverlay.tsx";
import environment from "../../environments/index";
import styles from "./ApplyExternalJobOffer.module.scss";
import {
  X,
  Mail,
  Copy,
  Check,
  ExternalLink,
  AlertCircle,
  FileText,
  User,
  Briefcase,
} from "lucide-react";
import type { WorkOffer } from "./WorkApplyModal.tsx";

interface ApplyExternalJobOfferProps {
  workOffer?: WorkOffer | null;
  isOpen: boolean;
  onClose: () => void;
}

const ApplyExternalJobOffer: React.FC<ApplyExternalJobOfferProps> = ({
  workOffer,
  isOpen,
  onClose,
}) => {
  const [emailCopied, setEmailCopied] = useState(false);

  const handleCopyEmail = async () => {
    try {
      const email = workOffer?.alternativeContact ?? workOffer?.user?.email ?? "";
      await navigator.clipboard.writeText(email);
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    } catch (err) {
      console.error("Error al copiar email:", err);
    }
  };

  const contactEmail = workOffer?.alternativeContact ?? workOffer?.user?.email ?? "";

  return (
    <ModalOverlay isOpen={isOpen} onClose={onClose}>
      <div className={styles.modal}>
            <header className={styles.header}>
              <div className={styles.headerContent}>
                <div className={styles.externalBadge}>
                  <ExternalLink size={16} />
                  <span>Oferta Externa</span>
                </div>
                <h2 className={styles.title}>¿Te interesa esta oportunidad?</h2>
                <p className={styles.subtitle}>
                  Esta es una búsqueda externa. Te compartimos la información de contacto para que
                  apliques directamente.
                </p>
              </div>
              <button className={styles.closeButton} onClick={onClose} aria-label="Cerrar">
                <X size={20} />
              </button>
            </header>
            <div className={styles.body}>
              {workOffer?.description && workOffer.description.trim() !== "" && (
                <div className={styles.descriptionSection}>
                  <h3 className={styles.sectionTitle}>
                    <FileText size={18} />
                    Descripción de la oferta
                  </h3>
                  <div className={styles.descriptionContent}>
                    <p>{workOffer.description}</p>
                  </div>
                </div>
              )}
              <div className={styles.contactSection}>
                <h3 className={styles.sectionTitle}>
                  <Mail size={18} />
                  Información de contacto
                </h3>
                <div className={styles.emailContainer}>
                  <div className={styles.emailInfo}>
                    <span className={styles.emailLabel}>Email de contacto:</span>
                    <span className={styles.emailAddress}>{contactEmail || "—"}</span>
                  </div>
                  <button
                    className={`${styles.copyButton} ${emailCopied ? styles.copied : ""}`}
                    onClick={handleCopyEmail}
                  >
                    {emailCopied ? <Check size={16} /> : <Copy size={16} />}
                    {emailCopied ? "Copiado" : "Copiar"}
                  </button>
                </div>
              </div>
              <div className={styles.tipsSection}>
                <h3 className={styles.sectionTitle}>
                  <AlertCircle size={18} />
                  Consejos para tu aplicación
                </h3>
                <div className={styles.tipsList}>
                  <div className={styles.tip}>
                    <FileText className={styles.tipIcon} />
                    <div className={styles.tipContent}>
                      <h4 className={styles.tipTitle}>Prepará tu CV</h4>
                      <p className={styles.tipDescription}>
                        Asegurate de que tu CV esté actualizado y destaque tu experiencia en
                        construcción.
                      </p>
                    </div>
                  </div>
                  <div className={styles.tip}>
                    <User className={styles.tipIcon} />
                    <div className={styles.tipContent}>
                      <h4 className={styles.tipTitle}>Carta de presentación</h4>
                      <p className={styles.tipDescription}>
                        Incluí una breve carta explicando por qué sos el candidato ideal para este
                        puesto.
                      </p>
                    </div>
                  </div>
                  <div className={styles.tip}>
                    <Briefcase className={styles.tipIcon} />
                    <div className={styles.tipContent}>
                      <h4 className={styles.tipTitle}>Mencioná Edify</h4>
                      <p className={styles.tipDescription}>
                        Indicá que encontraste esta oportunidad a través de la plataforma de Edify.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.footer}>
                <div className={styles.disclaimer}>
                  <AlertCircle size={16} />
                  <span>
                    Esta oferta es gestionada directamente por la empresa. Edify no participa en el
                    proceso de selección.
                  </span>
                </div>
                <p className={styles.signupLink}>
                  ¿Querés gestionar tus postulaciones y acceder a más ofertas?{" "}
                  <a
                    href={`${environment.appUrl ?? window.location.origin}/register`}
                    className={styles.signupLinkAnchor}
                  >
                    Creá tu cuenta completa en Edify
                  </a>
                </p>
                <div className={styles.footerButtons}>
                  <button className={styles.secondaryButton} onClick={onClose}>
                    Cerrar
                  </button>
                  <a
                    href={`mailto:${contactEmail}?subject=Aplicación para puesto de trabajo - Encontrado en Edify`}
                    className={styles.primaryButton}
                  >
                    <Mail size={16} />
                    Enviar Email
                  </a>
                </div>
              </div>
            </div>
      </div>
    </ModalOverlay>
  );
};

export default ApplyExternalJobOffer;
